import { useEffect, useMemo, useState } from 'react';
import { FiArchive } from 'react-icons/fi';
import FilterTable, { BaseData } from './Table/Table';
import Button from './atoms/Button';
import EmptyTable from './Table/EmptyTable';
import { useAcceptListGuestsMutation, useGetListGuestsQuery } from '../store';
import { toastSuccess } from '../utils/toasts';

interface Row extends BaseData {
  name: string | JSX.Element;
  status: string | JSX.Element;
  related_person: string | JSX.Element;
}

interface ListValidatorContentProps {
  listId: string;
}

const ListValidatorContent: React.FC<ListValidatorContentProps> = ({
  listId,
}) => {
  const { data: listGuests } = useGetListGuestsQuery(listId || '');
  const [acceptListGuests, { isSuccess: isAcceptListGuestsSuccess, isLoading: isAcceptListGuestsLoading }] = useAcceptListGuestsMutation();
  const [selectedGuests, setSelectedGuests] = useState<readonly string[]>([]);
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    if (isAcceptListGuestsSuccess) {
      setSelectedGuests([]);
      setTableKey((prev) => prev + 1);
      toastSuccess('Invitados actualizados correctamente');
    }
  }, [isAcceptListGuestsSuccess]);

  const sortedListGuests = useMemo(() => {
    if (!listGuests) { return []; }
    return [...listGuests].sort((a, b) => {
      if (a.status === 'accepted') { return 1; }
      if (b.status === 'accepted') { return -1; }
      return 0;
    });
  }, [listGuests]);

  const getStatusLabel = (status: string) => {
    if (status === 'accepted') {
      return <div className='tag-primary'>
        Ingresado
      </div>;
    }
    if (status === 'pending') {
      return <div className='tag-warning'>
        Pendiente
      </div>;
    }
    return '-';
  };

  const rows: Row[] = sortedListGuests?.map((guest) => ({
    id: guest.id,
    name: <span className='text-text-primary'>{guest.name}</span>,
    related_person: <span className='text-text-primary'>{guest.related_person}</span>,
    status: getStatusLabel(guest.status),
  })) || [];

  const headCells = [
    { id: 'name' as const, label: 'Nombre' },
    { id: 'related_person' as const, label: 'Persona relacionada' },
    { id: 'status' as const, label: 'Ingresado' },
  ];

  const acceptedCount = listGuests?.filter((guest) => guest.status === 'accepted').length || 0;

  return (
    <div className='glass-surface flex flex-col gap-6'>
      <div className='flex flex-row gap-2 items-center justify-between'>
        <h1 className='text-2xl font-medium'>Validador de lista</h1>
      </div>
      <p>Total de ingresos: {acceptedCount}</p>
      <div className='flex flex-row gap-2'>
        <Button
          variant='secondary'
          onClick={() => {
            setSelectedGuests([]);
            setTableKey((prev) => prev + 1);
          }}
          disabled={selectedGuests.length === 0}
        >
          Cancelar
        </Button>
        <Button
          variant='primary'
          loading={isAcceptListGuestsLoading}
          onClick={() => {
            acceptListGuests({ list_id: listId, list_guest_ids: selectedGuests as string[] });
          }}
          disabled={selectedGuests.length === 0}>
          Confirmar
        </Button>
      </div>
      {rows.length > 0 ? <FilterTable
        rows={rows}
        headCells={headCells}
        tableId='event-list-validator'
        highlightedRows
        allowSelect
        onSelectionChange={(selectedIds) => {
          setSelectedGuests(selectedIds);
        }}
        pagination={{ enabled: true, pageSize: 20 }}
        clearSelectionKey={tableKey}
      /> : (
        <EmptyTable
          title='No hay invitados'
          info='No hay invitados en la lista'
          icon={FiArchive}
          iconColor='text-primary-600'
        />
      )}
    </div>
  );
};

export default ListValidatorContent;
